/* eslint-disable @typescript-eslint/no-empty-function */
/* eslint-disable @typescript-eslint/no-explicit-any */
import axios, {
  AxiosInstance,
  AxiosRequestHeaders,
  AxiosResponse,
  AxiosRequestConfig,
} from "axios";
import { Message, Loading } from "element-ui";
import { blobValidate, downloadByBlob } from "./file";

interface ICommonResponse<T = any> {
  code: number;
  msg: string;
  data: T;
}

// 自定义请求配置
interface ICustomConfig {
  // 是否返回原数据，默认情况下会直接返回结果中的data，为true时会把整个返回结果抛出去
  original?: boolean;
}

interface IConstructorParams {
  baseUrl: string;
  timeout?: number;
  // 平台id
  bid: number | string;
  getBid: () => number | string;
  // 获取token方法
  getToken: () => string;
  // 获取租户code
  getTenantId: () => string;
  // 跳转回登录页方法
  redirectToLoginPage?: () => void;
}

// 初始化参数默认值
const httpConstructorDefaultParams: Required<IConstructorParams> = {
  baseUrl: "",
  timeout: 150000,
  bid: "",
  getBid: () => "",
  getToken: () => "",
  getTenantId: () => "",
  redirectToLoginPage: () => {},
};

/**
 * 封装公共HTTP请求类
 */
class Http {
  baseUrl = "";
  timeout: number;
  baseHeaders: AxiosRequestHeaders = {
    "Content-type": "application/json",
  };
  // 平台id
  bid: number | string;
  getBid: () => number | string;
  axiosInstance: AxiosInstance;

  // 获取token方法
  getToken: () => string;
  // 获取租户code
  getTenantId: () => string;
  // 跳转回登录页方法
  redirectToLoginPage: () => void;

  // 储存接口自定义配置参数
  requestCustomOptions: {
    [customOptionKey: string]: ICustomConfig;
  } = {};

  constructor(options: IConstructorParams) {
    const initOptions = {
      ...httpConstructorDefaultParams,
      ...options,
    };
    this.baseUrl = initOptions.baseUrl;
    this.timeout = initOptions.timeout;
    this.bid = initOptions.bid;
    this.getBid = initOptions.getBid;
    this.getToken = initOptions.getToken;
    this.getTenantId = initOptions.getTenantId;
    this.redirectToLoginPage = initOptions.redirectToLoginPage;

    this.axiosInstance = axios.create({
      baseURL: this.baseUrl,
      timeout: this.timeout,
    });
    this.initRequestInterceptors();
    this.initResponsetInterceptors();
  }

  // 初始化请求拦截器
  private initRequestInterceptors() {
    this.axiosInstance.interceptors.request.use(
      (config: AxiosRequestConfig) => {
        const token = this.getToken();
        config.headers = {
          ...this.baseHeaders,
          Authorization: token ? `Bearer ${token}` : "",
          "Tenant-Id": this.getTenantId(),
          bid: this.bid || this.getBid(),
          ...config.headers,
        };
        return config;
      },
      (err: any) => {
        return Promise.reject(err);
      }
    );
  }

  // 初始化响应拦截器
  private initResponsetInterceptors() {
    this.axiosInstance.interceptors.response.use(
      (res: AxiosResponse<ICommonResponse>) => {
        const customOptions =
          this.requestCustomOptions[this.getCustomOptionKey(res.config)] || {};
        const data = res.data;
        if (data.code !== 0 && data.code !== 200) {
          if (data.code === 401) {
            // 登录过期，重新登录
            this.redirectToLoginPage();
          } else {
            // 401重新登录不用提示
            Message({
              type: "error",
              message: data.msg || "操作失败",
            });
          }
          return Promise.reject(data);
        }
        if (customOptions.original) {
          return Promise.resolve(data);
        } else {
          if (data.data === undefined) {
            return Promise.resolve(data);
          } else {
            return Promise.resolve(data.data);
          }
        }
      },
      (err: any) => {
        if (err && err.response) {
          switch (err.response.status) {
            case 400:
              err.message = "请求错误";
              break;

            case 401:
              err.message = "未授权，请登录";
              // 登录过期，重新登录
              this.redirectToLoginPage();
              // 阻止往下执行弹窗
              return Promise.reject(err);

            case 403:
              err.message = "拒绝访问";
              break;

            case 404:
              err.message = `请求地址出错: ${err.response.config.url}`;
              break;

            case 408:
              err.message = "请求超时";
              break;

            case 500:
              err.message = "服务器内部错误";
              break;

            case 501:
              err.message = "服务未实现";
              break;

            case 502:
              err.message = "网关错误";
              break;

            case 503:
              err.message = "服务不可用";
              break;

            case 504:
              err.message = "网关超时";
              break;

            case 505:
              err.message = "HTTP版本不受支持";
              break;

            default:
          }
        }
        Message({
          type: "error",
          message: err.message || "请求错误",
        });
        return Promise.reject(err);
      }
    );
  }

  // 生成自定义参数储存key
  private getCustomOptionKey(config: AxiosRequestConfig): string {
    const key = `${config.method}-${config.url}`;
    return key;
  }

  // 发送请求
  request<T = any>(
    config: AxiosRequestConfig,
    customConfig?: ICustomConfig
  ): Promise<T> {
    if (customConfig) {
      this.requestCustomOptions[this.getCustomOptionKey(config)] = customConfig;
    }
    return this.axiosInstance.request(config);
  }

  get<T = any>(
    url: string,
    query?: any,
    headers?: AxiosRequestHeaders,
    customConfig?: ICustomConfig
  ): Promise<T> {
    const config: AxiosRequestConfig = {
      method: "get",
      url,
      params: query || {},
      headers: headers,
    };
    return this.request(config, customConfig);
  }

  post<T = any>(
    url: string,
    data?: any,
    query?: any,
    headers?: AxiosRequestHeaders,
    customConfig?: ICustomConfig
  ): Promise<T> {
    const config: AxiosRequestConfig = {
      method: "post",
      url,
      data: data || {},
      params: query || {},
      headers: headers,
    };
    return this.request(config, customConfig);
  }

  put<T = any>(
    url: string,
    data?: any,
    query?: any,
    headers?: AxiosRequestHeaders,
    customConfig?: ICustomConfig
  ): Promise<T> {
    const config: AxiosRequestConfig = {
      method: "put",
      url,
      data: data || {},
      params: query || {},
      headers: headers,
    };
    return this.request(config, customConfig);
  }

  delete<T = any>(
    url: string,
    query?: any,
    headers?: AxiosRequestHeaders,
    customConfig?: ICustomConfig
  ): Promise<T> {
    const config: AxiosRequestConfig = {
      method: "delete",
      url,
      params: query || {},
      headers: headers,
    };
    return this.request(config, customConfig);
  }

  exportFile(url: string, params: any, filename: string) {
    const token = this.getToken();
    const downloadLoadingInstance = Loading.service({
      text: "正在下载数据，请稍候",
    });
    return axios
      .post(`${this.baseUrl}${url}`, params, {
        headers: {
          "Content-type": "application/json",
          Authorization: token ? `Bearer ${token}` : "",
          "Tenant-Id": this.getTenantId(),
          bid: this.bid || this.getBid(),
        },
        responseType: "blob",
      })
      .then(async (res) => {
        const data = res.data;
        const isBlob = blobValidate(data);
        if (isBlob) {
          const blob = new Blob([data]);
          downloadByBlob(blob, filename);
        } else {
          const resText = await data.text();
          const rspObj = JSON.parse(resText);
          const errMsg = rspObj.msg;
          Message.error(errMsg);
        }
      })
      .catch((err) => {
        console.log("exportFile err...", err);
      })
      .finally(() => {
        downloadLoadingInstance.close();
      });
  }
}

export default Http;
